import React from 'react';

import PropTypes from 'prop-types';
import { MdInsertDriveFile } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { Tabs } from '@shared';
import { Button, Card, SearchInput, Tooltip } from '@shared/components';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

import { tabNames } from '../../constants';
import {
  activeTabSel,
  filteredRecentUnitsSel,
  filteredUnitsSel,
  recentUnitsSearchSel,
  recentUnitsSorterSel,
  removeRecentUnit,
  searchLoadingSel,
  searchSel,
  setActiveTab,
  setRecentUnit,
  setRecentUnitsSearch,
  setRecentUnitsSorter,
  setSearch,
  setSorter,
  sorterSel,
} from '../../duck';
import { openMBRModalVisible } from '../../duck/actions';
import MBRUploadModal from '../MBRUploadModal';
import CouncilImportFileStatus from './CouncilImportFileStatus';
import styles from './CouncilUnitPage.less';
import UnitSearch from './UnitSearch';
import UnitsList from './UnitsList';
import UnitsTable from './UnitsTable';

const { TabPane } = Tabs;

class CouncilUnitsPage extends React.PureComponent {
  handleSearch = search => {
    this.props.onSetSearch(search);
  };

  handleRecentUnitsSearch = search => {
    this.props.onSetRecentUnitsSearch(search);
  };

  handleSelectUnit = unit => {
    this.props.onSetUnit(unit);
  };

  handleSorterChange = tabName => sorter => {
    const { onSetSorter, onSetRecentUnitsSorter } = this.props;
    tabName === tabNames.UNITS
      ? onSetSorter(sorter)
      : onSetRecentUnitsSorter(sorter);
  };

  handleRemoveRecentUnit = organizationGuid => {
    this.props.onRemoveRecentUnit(organizationGuid);
  };

  handleMBRUpload = () => {
    this.props.onShowMBRModal();
  };

  render() {
    const {
      isMobile,
      items,
      loading,
      search,
      sorter,
      recentUnitsSorter,
      recentUnits,
      recentUnitsSearch,
      activeTab,
      onSetActiveTab,
    } = this.props;

    const showImportCouncilFile = featureFlags.getFlag(
      'SBL_6013_IMPORT_COUNCIL_FILE',
    ) ? (
      <div className={styles.extraContent}>
        <Tooltip
          title={
            <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.Tooltip" />
          }
        >
          <Button
            color="info"
            ghost
            noBorder
            fitText
            id="mbr_import"
            className={styles.button}
            uppercase={false}
            icon={<MdInsertDriveFile />}
            onClick={this.handleMBRUpload}
          >
            <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR" />
          </Button>
        </Tooltip>
      </div>
    ) : undefined;

    return (
      <div>
        <UnitSearch />
        <Card margin noPadding shadow>
          <Tabs
            size="small"
            activeKey={activeTab}
            onChange={onSetActiveTab}
            tabBarExtraContent={showImportCouncilFile}
          >
            <TabPane
              tab={intl.formatMessage({
                id: 'councilUnits.CouncilUnitsPage.unitsTab',
              })}
              key={tabNames.UNITS}
            >
              <SearchInput
                size="large"
                placeholder={intl.formatMessage({
                  id: 'councilUnits.CouncilUnitsPage.searchBy',
                })}
                value={search}
                onChange={this.handleSearch}
              />
              {isMobile ? (
                <UnitsList
                  items={items}
                  loading={loading}
                  sorter={sorter}
                  onSelectItem={this.handleSelectUnit}
                  onSorterChange={this.handleSorterChange(tabNames.UNITS)}
                />
              ) : (
                <UnitsTable
                  items={items}
                  loading={loading}
                  sorter={sorter}
                  onSelectItem={this.handleSelectUnit}
                  onSorterChange={this.handleSorterChange(tabNames.UNITS)}
                />
              )}
            </TabPane>
            <TabPane
              tab={intl.formatMessage({
                id: 'councilUnits.CouncilUnitsPage.recentSearchTab',
              })}
              key={tabNames.RECENT_UNITS}
            >
              <SearchInput
                size="large"
                placeholder={intl.formatMessage({
                  id: 'councilUnits.CouncilUnitsPage.recentUnits.searchBy',
                })}
                value={recentUnitsSearch}
                onChange={this.handleRecentUnitsSearch}
              />
              {isMobile ? (
                <UnitsList
                  items={recentUnits}
                  hasSearchedItems
                  loading={loading}
                  sorter={sorter}
                  onSelectItem={this.handleSelectUnit}
                  onSorterChange={this.handleSorterChange(
                    tabNames.RECENT_UNITS,
                  )}
                  onRemoveItem={this.handleRemoveRecentUnit}
                />
              ) : (
                <UnitsTable
                  items={recentUnits}
                  hasSearchedItems
                  sorter={recentUnitsSorter}
                  onSelectItem={this.handleSelectUnit}
                  onSorterChange={this.handleSorterChange(
                    tabNames.RECENT_UNITS,
                  )}
                  onRemoveItem={this.handleRemoveRecentUnit}
                />
              )}
            </TabPane>
            <TabPane
              tab={intl.formatMessage({
                id: 'councilUnits.CouncilUnitsPage.CouncilImportFileStatus',
              })}
              key={tabNames.COUNCIL_IMPORT_STATUS}
            >
              <CouncilImportFileStatus />
            </TabPane>
          </Tabs>
        </Card>
        <MBRUploadModal />
      </div>
    );
  }
}

CouncilUnitsPage.propTypes = {
  //Provided by parent
  //Provided by component itself
  isMobile: PropTypes.bool.isRequired,
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  search: PropTypes.string.isRequired,
  sorter: PropTypes.object.isRequired,
  recentUnitsSorter: PropTypes.object.isRequired,
  recentUnits: PropTypes.array.isRequired,
  recentUnitsSearch: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
  onSetSearch: PropTypes.func.isRequired,
  onSetUnit: PropTypes.func.isRequired,
  onSetSorter: PropTypes.func.isRequired,
  onSetRecentUnitsSorter: PropTypes.func.isRequired,
  onSetRecentUnitsSearch: PropTypes.func.isRequired,
  onRemoveRecentUnit: PropTypes.func.isRequired,
  onSetActiveTab: PropTypes.func.isRequired,
  onShowMBRModal: PropTypes.func.isRequired,
};

const mapState = state => ({
  isMobile: isMobileSel(state),
  items: filteredUnitsSel(state),
  loading: searchLoadingSel(state),
  search: searchSel(state),
  sorter: sorterSel(state),
  recentUnitsSorter: recentUnitsSorterSel(state),
  recentUnits: filteredRecentUnitsSel(state),
  recentUnitsSearch: recentUnitsSearchSel(state),
  activeTab: activeTabSel(state),
});

const mapDispatch = dispatch => ({
  onSetSearch: search => dispatch(setSearch(search)),
  onSetUnit: unit => dispatch(setRecentUnit(unit)),
  onSetSorter: sorter => dispatch(setSorter(sorter)),
  onSetRecentUnitsSorter: sorter => dispatch(setRecentUnitsSorter(sorter)),
  onSetRecentUnitsSearch: search => dispatch(setRecentUnitsSearch(search)),
  onRemoveRecentUnit: organizationGuid =>
    dispatch(removeRecentUnit(organizationGuid)),
  onSetActiveTab: tabName => dispatch(setActiveTab(tabName)),
  onShowMBRModal: () => dispatch(openMBRModalVisible()),
});

export default connect(mapState, mapDispatch)(CouncilUnitsPage);
